import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useProducts } from '@/hooks/useProducts';
import { ProductCard } from '@/components/shop/ProductCard';
import { Loader2 } from 'lucide-react';

interface RelatedProductsProps {
  currentHandle: string;
  tags: string[];
}

/**
 * Shows 4 related products from the same collection (based on shared tags).
 * Excludes the current product. Falls back gracefully if no matches.
 */
export function RelatedProducts({ currentHandle, tags }: RelatedProductsProps) {
  const { t } = useTranslation();

  // Determine which collection tag to use for filtering
  const collectionTag = useMemo(() => {
    const lower = tags.map(t => t.toLowerCase());
    if (lower.includes('montagna')) return 'tag:Montagna';
    if (lower.includes('fauna')) return 'tag:Fauna';
    if (lower.includes('equitazione')) return 'tag:equitazione';
    return null;
  }, [tags]);

  const { data: products, isLoading } = useProducts(20, collectionTag || undefined);

  const related = useMemo(() => {
    if (!products) return [];
    return products
      .filter(p => p.node.handle !== currentHandle)
      .slice(0, 4);
  }, [products, currentHandle]);

  if (!collectionTag || isLoading) {
    if (isLoading) {
      return (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-bronze" />
        </div>
      );
    }
    return null;
  }

  if (related.length === 0) return null;

  return (
    <section className="mt-16 pt-12 border-t border-border">
      <h2 className="font-display text-2xl md:text-3xl text-foreground text-center mb-8">
        {t('product.relatedProducts', 'You May Also Like')}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {related.map((product, index) => (
          <ProductCard key={product.node.id} product={product} index={index} />
        ))}
      </div>
    </section>
  );
}
